/**
 * AI Session Plan Generator — prompt template (§18.1)
 * Prompt version: 1.3 — 2026-05-24 — full-duration coverage + mandatory SSG
 * Changes from 1.2:
 *   - Rule 14: drills must sum to the requested session duration (±5 min).
 *   - Rule 15: every session must build to a SMALL_SIDED_GAME before the
 *     cool-down. (Brain eval found sessions stopping at isolated drills,
 *     leaving ~30 min unplanned with no game phase.)
 * Prompt version: 1.2 — 2026-05-06 — decision-making constraint on technical drills
 * Changes from 1.1:
 *   - Every TECHNICAL drill must specify the decision the player makes
 *     during execution (Ben Petty 2026-05-05 note 3 — "decision-making
 *     mandatory in technical drills"). New required field
 *     `decision_under_execution` on drills with type='TECHNICAL'.
 *   - Added rule 13 making this explicit + corresponding schema field.
 * Prompt version: 1.1 — 2026-04-30 — phase-of-play primary taxonomy
 * Changes from 1.0:
 *   - Added phase-of-play primary taxonomy (founder sync 2026-04-30):
 *     attacking | defending | transitions | individual.
 *   - Drills now tag a `primary_phase` and `sub_themes` alongside the
 *     existing corner profile. corner_coverage stays for analytics
 *     but phase_coverage is the primary balance metric.
 *   - Reframed coach archetype: grassroots-first, "less is more",
 *     voice-first.
 *
 * All prompts in /lib/prompts/ or /lib/ — never inline in components.
 */
import { PHASES_OF_PLAY, PHASE_SUB_THEMES } from './phaseOfPlay';

export const SESSION_PLANNER_PROMPT_VERSION = '1.3';

export interface SessionPlanInput {
  ageGroup: string;
  durationMins: number;
  playerCount: number;
  primaryFocus: string;
  secondaryFocus?: string;
  equipment: string[];
  seasonPhase: string;
  constraints?: string;
  /** Pre-rendered <priors> block — team principles, active curriculum,
   *  recent-session themes — from buildGenerationContext('session'). The
   *  generator must respect the priors: avoid repeating the last session's
   *  primary focus, route this session through at least one active
   *  principle, align with the curriculum phase if one exists. */
  priorsBlock?: string;
  /** Pre-rendered <relevant_frameworks> block from knowledgeSearch — top-K
   *  chunks from the KnowledgeBase keyed on the session's primary focus.
   *  Grounds the generated session in canonical coaching frameworks (FA
   *  4-Corner, 5Cs, Ben system, age-phase guidance). Optional — if empty,
   *  prompt falls back to hand-authored AGE_PHASE_CONTEXT only. */
  knowledgeBlock?: string;
}

export interface GeneratedDrill {
  name: string;
  type: 'WARM_UP' | 'TECHNICAL' | 'TACTICAL' | 'SMALL_SIDED_GAME' | 'COOL_DOWN';
  duration_mins: number;
  players_required: number;
  space: string;
  equipment: string[];
  setup_description: string;
  instructions: string;
  coaching_points: string[];
  progressions: string[];
  /** Founder sync 2026-04-30 — phase-of-play primary axis. Optional
   *  for back-compat with 1.0 outputs; required from 1.1 onwards. */
  primary_phase?: 'attacking' | 'defending' | 'transitions' | 'individual';
  sub_themes?: string[];
  corner_tags: ('technical_tactical' | 'physical' | 'psychological' | 'social')[];
  sub_corner_tags: string[];
  /** v1.2 — Required when `type === 'TECHNICAL'`. The decision the
   *  player makes DURING execution of the drill, in the player's own
   *  language. Per Ben Petty 2026-05-05 note 3: every technical drill
   *  must surface a decision so the work is decision-making, not
   *  isolated mechanical repetition. Examples:
   *    - "drive forward or pass to the supporting player?"
   *    - "first touch out of feet or to the open side?"
   *    - "shoot first time or take a touch?"
   *  Optional for non-TECHNICAL drill types. */
  decision_under_execution?: string;
}

export interface SessionPlanResult {
  session_title: string;
  session_objective: string;
  psych_social_intent: string;
  estimated_duration_mins: number;
  /** Founder sync 2026-04-30 — phase-of-play primary taxonomy. */
  primary_phase?: 'attacking' | 'defending' | 'transitions' | 'individual';
  sub_themes?: string[];
  /** Optional in 1.0 schema; required from prompt 1.1 onwards. */
  phase_coverage?: {
    attacking: number;
    defending: number;
    transitions: number;
    individual: number;
  };
  corner_coverage: {
    technical_tactical: number;
    physical: number;
    psychological: number;
    social: number;
  };
  drills: GeneratedDrill[];
  coach_focus_questions: string[];
  parent_summary: string;
}

// Age-phase emphasis from KnowledgeBase/synthesis/4-corner-canonical-mapping.md
const AGE_PHASE_CONTEXT = `
Age-phased emphasis (FA 4 Corner Model):
| Age      | Game Format | Tech-Tact bias                    | Physical bias                 | Psych-Social bias (5Cs)                        |
|----------|-------------|-----------------------------------|-------------------------------|-------------------------------------------------|
| U7-U8    | 5v5         | Me and the ball — basics          | General coordination          | Fun + Commitment + Communication                |
| U9-U10   | 7v7         | Small team play — combinations    | Specific coordination         | Concentration + Communication                   |
| U11-U12  | 9v9         | Team game — decision-making       | Speed + timing                | Control + Concentration + Confidence            |
| U13-U14  | 11v11       | Full game — tactical principles   | Speed + strength              | All 5Cs balanced                                |
| U15-U16  | 11v11       | Game model — systems of play      | Flexibility + power           | All 5Cs under pressure                          |
| U17-U19+ | 11v11       | Performance — game management     | Stamina + robustness          | Confidence + Control under pressure             |
`;

export function buildSessionPlanPrompt(input: SessionPlanInput): string {
  const phaseSubThemesBlock = PHASES_OF_PLAY
    .map((p) => `  ${p}: ${PHASE_SUB_THEMES[p].join(', ')}`)
    .join('\n');

  return `You are an expert football coach curriculum designer for grassroots and academy coaches (NOT first-team / pro). You think in PHASES OF PLAY first, FA 4 Corner Model second.

Generate a complete session plan matching these requirements:
- Age group: ${input.ageGroup}
- Session duration: ${input.durationMins} minutes
- Number of players: ${input.playerCount}
- Primary focus: ${input.primaryFocus}
${input.secondaryFocus ? `- Secondary focus: ${input.secondaryFocus}` : ''}
- Equipment available: ${input.equipment.join(', ') || 'cones, balls, bibs, goals'}
- Season phase: ${input.seasonPhase}
${input.constraints ? `- Constraints: ${input.constraints}` : ''}

${AGE_PHASE_CONTEXT}

Phase-of-play taxonomy (PRIMARY drill organising axis — founder sync 2026-04-30):
  attacking    — in possession; building, progressing, finishing
  defending    — out of possession; pressing, blocking, recovering
  transitions  — moments of turnover (attack→defence, defence→attack)
  individual   — player-development work crossing all phases (1v1, technique, decision-making, mental)

Sub-themes per phase (use these labels when tagging drills; the AI is free to add new ones if the source clearly demands it):
${phaseSubThemesBlock}

Sub-corners available:
Technical/Tactical: First Touch & Passing, Passing Tempo, Passing Variation, Playing Out from the Back, Finishing, 1v1 Attack, Dribbling & Creativity, Pressing, Defensive Shape, Decision-Making, Tempo Control, Body Orientation, Off-Ball Positioning, Support Triangles, Passing Lines, Pressing Trigger, Marking Recognition, Transition Reading
Physical: Acceleration, Mid-Distance Speed, Long-Distance Speed, Change of Pace, Agility, Coordination, Holding Strength, Duel Strength, Aerial Strength, Aerobic Endurance, Repeat Sprint Endurance, Flexibility, Reaction Time
Psychological: Commitment, Concentration, Control, Confidence, Coachability, Growth Mindset, Resilience, Intrinsic Motivation
Social: Communication, Teamwork, Peer Encouragement, Leadership Voice, Inclusivity, Respect, Cultural Belonging

Rules:
1. corner_coverage must sum to 100 (analytics axis)
2. phase_coverage must sum to 100 (PRIMARY balance metric — what proportion of session time develops each phase of play)
3. EVERY drill must carry a single primary_phase ∈ {attacking, defending, transitions, individual} and a non-empty sub_themes[] using labels from the lists above where possible
4. psych_social_intent is REQUIRED — what is the mental/social learning intent?
5. Every drill must tag at least one corner
6. Use standard football coaching terminology
7. Coaching points must be specific and actionable, not generic. "Less is more" — 2–4 high-quality coaching points per drill beats 8 generic ones
8. Space dimensions in yards (e.g. "20x15 yard grid")
9. Include a WARM_UP drill first and a COOL_DOWN drill last
10. sub_corner_tags must use exact labels from the list above
11. If <priors> are provided: vary primary focus from the most recent
    session(s), route at least one drill through an active principle,
    and align with the curriculum phase if present. If you must
    deviate from a prior, call it out in session_objective.
12. Coach archetype: grassroots / academy — non-technical user. Output must be runnable on a phone in 5 minutes. Don't lecture or pad.
13. DECISION-MAKING IN TECHNICAL DRILLS (Ben Petty 2026-05-05 note 3):
    every drill with type='TECHNICAL' MUST set decision_under_execution
    to a single concrete decision the player makes WHILE doing the
    drill, phrased as a question the player can hold in their head.
    Good: "drive forward or pass to the supporting player?", "first
    touch out of feet or to the open side?". Bad: "improve technique"
    (not a decision), "play forward when possible" (not a question).
    A purely mechanical rep with no decision is never an acceptable
    technical drill — re-design it as a constrained 1v1 / 2v1 / rondo
    so a defender, target, or rule forces the choice. This is
    optional for WARM_UP / TACTICAL / SMALL_SIDED_GAME / COOL_DOWN.
14. FILL THE WHOLE SESSION: the drills' duration_mins MUST sum to the
    requested ${input.durationMins}-minute session, within ±5 minutes.
    estimated_duration_mins must match that sum. Never leave time
    unplanned — extend the main phase or add a progression rather than
    stopping early.
15. ALWAYS BUILD TO A GAME: every session MUST include at least one
    SMALL_SIDED_GAME (the main, game-realistic phase) before the COOL_DOWN.
    A warm-up plus isolated technical drills is incomplete — the work has
    to transfer into a game.

${input.priorsBlock ?? ''}

${input.knowledgeBlock ?? ''}

Return ONLY valid JSON matching this schema — no prose, no markdown fences:
{
  "session_title": string,
  "session_objective": string,
  "psych_social_intent": string,
  "estimated_duration_mins": number,
  "primary_phase": "attacking" | "defending" | "transitions" | "individual",
  "sub_themes": string[],
  "phase_coverage": { "attacking": number, "defending": number, "transitions": number, "individual": number },
  "corner_coverage": { "technical_tactical": number, "physical": number, "psychological": number, "social": number },
  "drills": [
    {
      "name": string,
      "type": "WARM_UP" | "TECHNICAL" | "TACTICAL" | "SMALL_SIDED_GAME" | "COOL_DOWN",
      "duration_mins": number,
      "players_required": number,
      "space": string,
      "equipment": string[],
      "setup_description": string,
      "instructions": string,
      "coaching_points": string[],
      "progressions": string[],
      "primary_phase": "attacking" | "defending" | "transitions" | "individual",
      "sub_themes": string[],
      "corner_tags": ("technical_tactical" | "physical" | "psychological" | "social")[],
      "sub_corner_tags": string[],
      "decision_under_execution": string  // REQUIRED when type='TECHNICAL'; omit otherwise
    }
  ],
  "coach_focus_questions": string[],
  "parent_summary": string
}`;
}

/**
 * Validate that an AI-generated session plan satisfies prompt v1.2 rule 13:
 * every TECHNICAL drill must set a non-empty decision_under_execution.
 *
 * Returns the list of drill indices that failed the rule. Empty array means
 * the plan passes. Caller decides whether to re-prompt the AI, surface a
 * coach-facing warning, or block the save.
 */
export function findTechnicalDrillsMissingDecision(
  drills: readonly Pick<GeneratedDrill, 'type' | 'decision_under_execution'>[],
): { index: number; reason: 'missing' | 'empty' }[] {
  const failures: { index: number; reason: 'missing' | 'empty' }[] = [];
  drills.forEach((d, i) => {
    if (d.type !== 'TECHNICAL') return;
    if (d.decision_under_execution == null) {
      failures.push({ index: i, reason: 'missing' });
      return;
    }
    if (d.decision_under_execution.trim().length === 0) {
      failures.push({ index: i, reason: 'empty' });
    }
  });
  return failures;
}

/**
 * Structural completeness checks (prompt v1.3): the drills should fill the
 * requested duration (±5 min) and build to a small-sided game before the
 * cool-down. Returns human-readable gap descriptions; empty array = complete.
 */
export function findSessionStructureGaps(
  result: { drills: readonly Pick<GeneratedDrill, 'type' | 'duration_mins'>[]; estimated_duration_mins?: number },
  requestedMins?: number,
): string[] {
  const gaps: string[] = [];
  const drills = result.drills ?? [];
  const total = drills.reduce((sum, d) => sum + (Number(d.duration_mins) || 0), 0);
  const target = requestedMins ?? result.estimated_duration_mins;
  if (target && Math.abs(total - target) > 5) {
    gaps.push(`drills sum to ${total} min but the session is ${target} min`);
  }
  if (!drills.some((d) => d.type === 'SMALL_SIDED_GAME')) {
    gaps.push('no SMALL_SIDED_GAME phase (session never builds to a game)');
  }
  return gaps;
}
